'use client'

/**
 * Global Error Boundary (Root Layout)
 *
 * This is a special error boundary that catches errors in the ROOT LAYOUT.
 * It must define its own <html> and <body> tags because the root layout
 * may have failed to render.
 *
 * Reports errors to Sentry for tracking.
 * This is rarely triggered but important for catastrophic failures.
 */

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'

/** Detect Next.js chunk load failures (e.g. stale deployment, network blip) */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message ?? ''
  return (
    msg.includes('Failed to load chunk') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    /ChunkLoadError|Loading chunk \d+ failed/.test(msg)
  )
}

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  const isChunkError = isChunkLoadError(error)

  useEffect(() => {
    // Log to console for local debugging (Sentry reporting below handles production tracking)
    // Note: Using explicit Sentry.captureException instead of logger.error() to include
    // richer metadata (level: 'fatal', severity tags) that logger.error() doesn't support
    console.error('Root layout error:', error)

    // Report to Sentry - critical error in root layout
    Sentry.captureException(error, {
      level: 'fatal',
      tags: {
        errorBoundary: 'root-layout',
        digest: error.digest,
        severity: 'critical',
        isChunkLoadError: String(isChunkError),
      },
    })
  }, [error, isChunkError])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          padding: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f9fafb',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: '400px',
            width: '100%',
            padding: '24px',
            textAlign: 'center',
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 24px',
              backgroundColor: '#fee2e2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          {/* Message */}
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#111827',
              marginBottom: '8px',
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '24px',
            }}
          >
            {isChunkError
              ? 'A page failed to load. Refreshing will fetch the latest version.'
              : 'A critical error occurred. Please try refreshing the page.'}
          </p>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={() =>
                isChunkError ? window.location.reload() : reset()
              }
              style={{
                padding: '10px 20px',
                backgroundColor: '#14b891',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => (window.location.href = '/')}
              style={{
                padding: '10px 20px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Go to homepage
            </button>
          </div>

          {/* Error ID */}
          {error.digest && (
            <p
              style={{
                fontSize: '12px',
                color: '#9ca3af',
                marginTop: '24px',
              }}
            >
              Error ID: {error.digest}
            </p>
          )}
        </div>
      </body>
    </html>
  )
}
